import React, { type RefObject } from 'react';
import type { RouterActionsType } from '@atlassian/react-resource-router';
import { ENTITY_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';

import type { NestedItemDragAndDrop } from '@atlassian/navigation-drag-and-drop';
import type { JswBoardsRefactored$data } from './__generated__/JswBoardsRefactored.graphql.ts';
import { BoardLinkMenuItem, type BoardLinkMenuItemProps } from './BoardLinkMenuItem.tsx';

// Type for board edge data structure
type BoardEdge = NonNullable<
	NonNullable<
		NonNullable<NonNullable<JswBoardsRefactored$data['jira']>['jiraProjectByKey']>['boards']
	>['edges']
>[0];

export interface BoardItemProps {
	edge: BoardEdge;
	index: number;
	projectKey: string;
	project: NonNullable<JswBoardsRefactored$data['jira']>['jiraProjectByKey'];
	isAnyBoardSelected: boolean;
	isProjectScoped: boolean;
	mostRecentlyViewedBoardId: number | null | undefined;
	isSelectedPath: (path: string) => boolean;
	isControlledPath: (path: string) => boolean;
	handleBoardClick: (boardId: number) => () => void;
	routerPush: RouterActionsType['push'];
	boardActions: React.ReactNode;
	url?: string | null;
	dragAndDropState?: NestedItemDragAndDrop;
}

export function BoardItem({
	dragAndDropState,
	edge,
	index,
	projectKey,
	project,
	isAnyBoardSelected,
	isProjectScoped,
	mostRecentlyViewedBoardId,
	isSelectedPath,
	isControlledPath,
	handleBoardClick,
	routerPush,
	boardActions,
	url,
}: BoardItemProps) {
	if (!project) {
		return null;
	}

	if (!edge?.node?.boardId) {
		return null;
	}

	const boardNode = edge.node;
	const href = buildBoardHref(projectKey, url);
	const name = boardNode.name;
	const boardId = boardNode.boardId?.toString() || '';

	const isSelected =
		isSelectedPath(ENTITY_ID.BOARD(boardId)) ||
		isControlledPath(ENTITY_ID.BOARD(boardId)) ||
		(isProjectScoped && edge?.node?.boardId === mostRecentlyViewedBoardId);

	const shouldAutoSelect = index === 0 && !isAnyBoardSelected;

	const mappedDragAndDropState: BoardLinkMenuItemProps['dragAndDrop'] = {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		draggableAnchorRef: dragAndDropState?.draggableRef as unknown as RefObject<HTMLAnchorElement>,
		isDragging: dragAndDropState?.isDragging || false,
		hasDragIndicator: dragAndDropState?.isItemDraggable || false,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		visualContentRef: dragAndDropState?.dropTargetRef as unknown as RefObject<HTMLDivElement>,
		dropIndicator: dragAndDropState?.dropIndicator || null,
	};

	return (
		<div key={edge?.node?.id}>
			<BoardLinkMenuItem
				boardId={boardId}
				name={name || ''}
				href={href}
				isSelected={isSelected}
				actionsOnHover={boardActions}
				onClick={handleBoardClick(edge?.node?.boardId || 0)}
				shouldAutoSelect={shouldAutoSelect}
				routerPush={routerPush}
				dragAndDrop={mappedDragAndDropState}
			/>
			{dragAndDropState && dragAndDropState.maybeDragPreview}
		</div>
	);
}

function buildBoardHref(
	projectKey: string,
	url?: string | null,
): { to: string; prefetch: 'hover' } {
	return {
		to: url ?? `/browse/${projectKey}`,
		prefetch: 'hover',
	};
}
